import { CheckIcon, WarningIcon } from '@phosphor-icons/react';
import { useRef, useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ProgressSegmentState } from '@/lib/content-doctrine/article-progress';
import { cn } from '@/lib/utils';

export interface SegmentedRingSegment {
  id: string;
  label: string;
  state: ProgressSegmentState;
  detail: string;
}

const SIZES = {
  sm: { box: 20, stroke: 3, hairline: 1, gap: 3, glyph: 'size-2.5' },
  md: { box: 30, stroke: 3.5, hairline: 1.25, gap: 4, glyph: 'size-3.5' },
} as const;

const MARK_CLASS: Record<ProgressSegmentState, string> = {
  done: 'bg-foreground/70',
  partial: 'border border-foreground/70 bg-foreground/30',
  attested: 'border border-dashed border-info',
  warning: 'bg-warning',
  todo: 'border border-muted-foreground/60',
  'n/a': 'h-0.5 w-2 bg-muted-foreground',
};

function isSettled(state: ProgressSegmentState): boolean {
  return state === 'done' || state === 'attested';
}

function arcTone(state: ProgressSegmentState, complete: boolean): string {
  if (state === 'warning') return 'text-warning';
  if (state === 'attested') return 'text-info';
  return complete ? 'text-success' : 'text-foreground/70';
}

export function SegmentedRing({
  segments,
  ariaLabel,
  nextStep,
  describedById,
  size = 'sm',
  focusable = true,
  forceOpen = false,
  side = 'right',
  align = 'start',
  className,
}: {
  segments: readonly SegmentedRingSegment[];
  ariaLabel: string;
  nextStep?: string | null;
  describedById?: string;
  size?: keyof typeof SIZES;
  /**
   * The ring is its own tab stop so the tooltip is reachable without a pointer.
   * Turn it off only inside a parent that is already a button (nested focusables
   * are invalid) and drive the tooltip from that parent with `forceOpen`.
   */
  focusable?: boolean;
  forceOpen?: boolean;
  /**
   * Where the legend opens relative to the ring. The default is beside it, top-aligned to it, so it grows
   * AWAY from the ring into the row. It used to be `top` + `end`, which aligns the card's RIGHT edge to the
   * ring and lets a 320px card grow leftward: on the Map, where the ring is the leftmost thing in the row,
   * that put the card over the app sidebar at the far left of the viewport.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
}): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const [touchOpen, setTouchOpen] = useState(false);
  const pointerType = useRef('mouse');
  const { box, stroke, hairline, gap: baseGap, glyph } = SIZES[size];
  const applicable = segments.filter((segment) => segment.state !== 'n/a');
  const count = applicable.length;
  const doneCount = applicable.filter((segment) => isSettled(segment.state)).length;
  const complete = count > 0 && doneCount === count;
  // Seven or eight arcs on a 20px ring would each be under 4px long with the usual gap, so the gaps close up.
  const gap = count > 6 ? baseGap * 0.67 : baseGap;
  const slots = Math.max(1, count);
  const radius = (box - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const step = circumference / slots;
  const arcLength = step - gap;

  const hasWarning = segments.some((segment) => segment.state === 'warning');
  const dash = arcLength / 5;

  const arc = (index: number, length: number, width: number, tone: string, dashed = false) => (
    <circle
      cx={box / 2}
      cy={box / 2}
      r={radius}
      fill="none"
      strokeWidth={width}
      strokeDasharray={
        dashed
          ? `${dash} ${dash} ${dash} ${dash} ${dash} ${circumference - length}`
          : `${length} ${circumference - length}`
      }
      strokeDashoffset={-(index * step + gap / 2)}
      className={cn('stroke-current', tone)}
    />
  );

  return (
    <>
      <Tooltip
        open={hovered || touchOpen || forceOpen}
        onOpenChange={(open) => {
          setHovered(open);
          if (!open) setTouchOpen(false);
        }}
      >
        <TooltipTrigger asChild>
          <span
            className={cn(
              'relative inline-flex shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
              className,
            )}
            style={{ width: box, height: box }}
            role="img"
            tabIndex={focusable ? 0 : undefined}
            onPointerDown={(event) => {
              pointerType.current = event.pointerType;
            }}
            onClick={(event) => {
              // A tap has no hover, so on touch the ring opens its own legend instead of
              // falling through to the row that opens the dock.
              if (pointerType.current !== 'touch') return;
              event.stopPropagation();
              event.preventDefault();
              setTouchOpen((open) => !open);
            }}
            data-complete={complete}
            aria-label={ariaLabel}
          >
            <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} className="-rotate-90">
              <circle
                cx={box / 2}
                cy={box / 2}
                r={radius}
                fill="none"
                strokeWidth={hairline}
                className="stroke-current text-muted-foreground/40"
              />
              {applicable.map((segment, index) => (
                <g key={segment.id}>
                  {segment.state === 'done' || segment.state === 'warning'
                    ? arc(index, arcLength, stroke, arcTone(segment.state, complete))
                    : null}
                  {segment.state === 'partial'
                    ? arc(index, arcLength / 2, stroke, arcTone(segment.state, complete))
                    : null}
                  {segment.state === 'attested'
                    ? arc(index, arcLength, stroke, arcTone(segment.state, complete), true)
                    : null}
                </g>
              ))}
            </svg>
            {complete ? (
              <CheckIcon weight="bold" aria-hidden className={cn('absolute text-success', glyph)} />
            ) : hasWarning ? (
              <WarningIcon
                weight="bold"
                aria-hidden
                className={cn('absolute text-warning', glyph)}
              />
            ) : null}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={16}
          className="space-y-1.5 py-2 text-caption"
        >
          <p className="font-medium text-foreground">
            {doneCount} of {count} steps
            {count < segments.length ? (
              <span className="font-normal text-muted-foreground">
                {' '}
                · {segments.length - count} not applicable, left out of the ring
              </span>
            ) : null}
          </p>
          <ul className="space-y-1">
            {segments.map((segment) => (
              <li key={segment.id} className="flex items-start gap-2">
                <span
                  aria-hidden
                  className={cn(
                    'shrink-0',
                    segment.state === 'n/a' ? 'mt-1.75' : 'mt-1 size-2 rounded-full',
                    MARK_CLASS[segment.state],
                  )}
                />
                <span className="min-w-0">
                  <span
                    className={cn(
                      segment.state === 'n/a' ? 'text-muted-foreground' : 'text-foreground',
                    )}
                  >
                    {segment.label}
                  </span>
                  <span className="block text-muted-foreground">{segment.detail}</span>
                </span>
              </li>
            ))}
          </ul>
          {nextStep ? (
            <p className="border-t border-border/40 pt-1.5 text-muted-foreground">
              Next: <span className="text-foreground">{nextStep}</span>
            </p>
          ) : null}
        </TooltipContent>
      </Tooltip>
      {describedById ? (
        <span id={describedById} className="sr-only">
          {ariaLabel}
        </span>
      ) : null}
    </>
  );
}
